import {
    useState
} from "react";
import {
    useWindowDimensions,
    StyleSheet,
    View
} from "react-native";
import {
    WorkspaceSwitcher
} from "./brand";
import {
    NCoreUIKitLocalize,
    type IUploadFile,
    NCoreUIKitTheme,
    type ITreeNode,
    DropdownMenu,
    ToggleGroup,
    Breadcrumb,
    FileUpload,
    TextInput,
    Separator,
    TreeView,
    Progress,
    Sidebar,
    Avatar,
    Button,
    Table,
    Card,
    Text
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../i18n";
import {
    EllipsisVertical,
    FolderPlus,
    LayoutGrid,
    FileImage,
    FileText,
    Download,
    Pencil,
    Search,
    Folder,
    Upload,
    Trash2,
    Link,
    List,
    Move,
    Eye
} from "lucide-react-native";
import {
    createKitIcon
} from "../components/kitIcons";

type FileRow = {
    kind: "image" | "document";
    modified: Date;
    owner: string;
    size: number;
    name: string;
    id: string;
};

const FolderIcon = createKitIcon(Folder);

const FolderPlusIcon = createKitIcon(FolderPlus);

const EllipsisVerticalIcon = createKitIcon(EllipsisVertical);

const SearchIcon = createKitIcon(Search);

const UploadIcon = createKitIcon(Upload);

const ListIcon = createKitIcon(List);

const LayoutGridIcon = createKitIcon(LayoutGrid);

const FileImageIcon = createKitIcon(FileImage);

const FileTextIcon = createKitIcon(FileText);

const EyeIcon = createKitIcon(Eye);

const LinkIcon = createKitIcon(Link);

const PencilIcon = createKitIcon(Pencil);

const MoveIcon = createKitIcon(Move);

const TrashIcon = createKitIcon(Trash2);

const DownloadIcon = createKitIcon(Download);

const KILOBYTE = 1024;

const MEGABYTE = KILOBYTE * 1024;

const DEFAULT_PATH = [
    "design",
    "campaigns",
    "winter"
];

const PATHS: Record<string, Array<string>> = {
    summer: [
        "design",
        "campaigns",
        "summer"
    ],
    winter: [
        "design",
        "campaigns",
        "winter"
    ],
    campaigns: [
        "design",
        "campaigns"
    ],
    documents: [
        "documents"
    ],
    brand: [
        "design",
        "brand"
    ],
    design: [
        "design"
    ],
    shared: [
        "shared"
    ]
};

const FILES: Array<FileRow> = [
    {
        modified: new Date(2026, 10, 1, 10, 42),
        name: "kis-kampanyasi-afis.png",
        size: 4.8 * MEGABYTE,
        owner: "Elif Demir",
        kind: "image",
        id: "file1"
    },
    {
        modified: new Date(2026, 9, 31, 17, 15),
        name: "ana-banner-1920x1080.png",
        size: 3.2 * MEGABYTE,
        owner: "Mert Kaya",
        kind: "image",
        id: "file2"
    },
    {
        modified: new Date(2026, 9, 31, 9, 30),
        name: "kampanya-brifi.pdf",
        owner: "Zeynep Arslan",
        size: 1.2 * MEGABYTE,
        kind: "document",
        id: "file3"
    },
    {
        modified: new Date(2026, 9, 30, 18, 2),
        name: "kis-katalogu-2026.pdf",
        size: 18.6 * MEGABYTE,
        owner: "Can Öztürk",
        kind: "document",
        id: "file4"
    },
    {
        modified: new Date(2026, 9, 29, 14, 48),
        name: "urun-fotografi-set-01.png",
        size: 6.4 * MEGABYTE,
        owner: "Elif Demir",
        kind: "image",
        id: "file5"
    },
    {
        modified: new Date(2026, 9, 28, 11, 20),
        name: "logo-beyaz.png",
        owner: "Ada Lovelace",
        size: 212 * KILOBYTE,
        kind: "image",
        id: "file6"
    },
    {
        modified: new Date(2026, 9, 27, 16, 37),
        name: "butce-plani-q4.pdf",
        size: 842 * KILOBYTE,
        owner: "Mert Kaya",
        kind: "document",
        id: "file7"
    },
    {
        modified: new Date(2026, 9, 24, 10, 5),
        name: "sosyal-medya-takvimi.pdf",
        owner: "Zeynep Arslan",
        size: 2.1 * MEGABYTE,
        kind: "document",
        id: "file8"
    }
];

const INITIAL_UPLOADS: Array<IUploadFile> = [
    {
        name: "story-gorseli-02.png",
        size: 4.8 * MEGABYTE,
        status: "uploading",
        key: "upload1",
        progress: 72
    },
    {
        name: "kis-kampanyasi-brifi.pdf",
        size: 1.2 * MEGABYTE,
        key: "upload2",
        status: "done",
        progress: 100
    }
];

const stylesheet = StyleSheet.create({
    bar: {
        alignItems: "center",
        flexDirection: "row",
        flexWrap: "wrap"
    },
    person: {
        alignItems: "center",
        flexDirection: "row"
    },
    grid: {
        flexDirection: "row",
        flexWrap: "wrap"
    },
    cell: {
        minWidth: 0,
        flexGrow: 1
    },
    grow: {
        minWidth: 0,
        flex: 1
    },
    search: {
        minWidth: 0,
        flex: 1
    },
    content: {
        minWidth: 0
    }
});

const handlePress = () => undefined;

const Files01 = () => {
    const {
        t
    } = useDocsI18n();

    const {
        activeLocale
    } = NCoreUIKitLocalize.useContext();

    const {
        controlHeights,
        shapeConfigs,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        width
    } = useWindowDimensions();

    const [
        selectedFolder,
        setSelectedFolder
    ] = useState<Array<string>>([
        "winter"
    ]);

    const [
        expandedKeys,
        setExpandedKeys
    ] = useState<Array<string>>([
        "design",
        "campaigns"
    ]);

    const [
        view,
        setView
    ] = useState("list");

    const [
        query,
        setQuery
    ] = useState("");

    const [
        selectedFile,
        setSelectedFile
    ] = useState("file4");

    const [
        uploads,
        setUploads
    ] = useState<Array<IUploadFile>>(INITIAL_UPLOADS);

    const dateFormat = new Intl.DateTimeFormat(activeLocale, {
        dateStyle: "medium",
        timeStyle: "short"
    });

    const megabytes = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 1,
        unit: "megabyte",
        style: "unit"
    });

    const gigabytes = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 1,
        unit: "gigabyte",
        style: "unit"
    });

    const kilobytes = new Intl.NumberFormat(activeLocale, {
        maximumFractionDigits: 0,
        unit: "kilobyte",
        style: "unit"
    });

    const isCompact = width < shapeConfigs.compactBreakpoint;

    const folderKey = selectedFolder[0] ?? "winter";

    const path = PATHS[folderKey] ?? DEFAULT_PATH;

    const nodes: Array<ITreeNode> = [
        {
            children: [
                {
                    title: t("blocks.files01.folder.brand"),
                    icon: FolderIcon,
                    key: "brand"
                },
                {
                    children: [
                        {
                            title: t("blocks.files01.folder.winter"),
                            icon: FolderIcon,
                            key: "winter",
                            meta: "8"
                        },
                        {
                            title: t("blocks.files01.folder.summer"),
                            icon: FolderIcon,
                            key: "summer"
                        }
                    ],
                    title: t("blocks.files01.folder.campaigns"),
                    icon: FolderIcon,
                    key: "campaigns"
                }
            ],
            title: t("blocks.files01.folder.design"),
            icon: FolderIcon,
            key: "design"
        },
        {
            title: t("blocks.files01.folder.documents"),
            icon: FolderIcon,
            key: "documents"
        },
        {
            title: t("blocks.files01.folder.shared"),
            icon: FolderIcon,
            key: "shared"
        }
    ];

    const normalizedQuery = query.trim().toLocaleLowerCase(activeLocale);

    const files = FILES.filter((file) => normalizedQuery === "" || file.name.toLocaleLowerCase(activeLocale).includes(normalizedQuery));

    const formatSize = (size: number) => size >= MEGABYTE ? megabytes.format(size / MEGABYTE) : kilobytes.format(size / KILOBYTE);

    const handleSelect = (keys: Array<string>) => setSelectedFolder(keys.length > 0 ? keys : selectedFolder);

    const handleViewChange = (value: string | Array<string>) => setView(typeof value === "string" && value !== "" ? value : view);

    const handleFileSelect = (id: string) => () => setSelectedFile(id);

    const renderFileMenu = (file: FileRow) => <DropdownMenu
        title={t("blocks.files01.fileActions", [
            file.name
        ])}
    >
        <DropdownMenu.Trigger>
            <Button
                accessibilityLabel={t("blocks.files01.fileActions", [
                    file.name
                ])}
                icon={EllipsisVerticalIcon}
                onPress={handlePress}
                variant="ghost"
                type="neutral"
                size="small"
            />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
            <DropdownMenu.Item
                title={t("blocks.files01.open")}
                onPress={handlePress}
                shortcut="enter"
                icon={EyeIcon}
            />
            <DropdownMenu.Item
                title={t("blocks.files01.copyLink")}
                onPress={handlePress}
                icon={LinkIcon}
            />
            <DropdownMenu.Item
                title={t("blocks.files01.download")}
                onPress={handlePress}
                icon={DownloadIcon}
            />
            <DropdownMenu.Item
                title={t("blocks.files01.rename")}
                onPress={handlePress}
                icon={PencilIcon}
                shortcut="F2"
            />
            <DropdownMenu.Item
                title={t("blocks.files01.move")}
                onPress={handlePress}
                icon={MoveIcon}
            />
            <DropdownMenu.Separator/>
            <DropdownMenu.Item
                title={t("blocks.files01.delete")}
                onPress={handlePress}
                shortcut="backspace"
                icon={TrashIcon}
                type="danger"
            />
        </DropdownMenu.Content>
    </DropdownMenu>;

    const renderRow = (file: FileRow) => <Table.Row
        isSelected={selectedFile === file.id}
        onPress={handleFileSelect(file.id)}
        key={file.id}
    >
        <Table.Cell>
            <View
                style={[
                    stylesheet.person,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Avatar
                    icon={file.kind === "image" ? FileImageIcon : FileTextIcon}
                    size="xSmall"
                />
                <Text
                    variant="labelMediumSize"
                    color="high"
                >
                    {file.name}
                </Text>
            </View>
        </Table.Cell>
        <Table.Cell>
            <View
                style={[
                    stylesheet.person,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Avatar
                    title={file.owner}
                    size="xSmall"
                />
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                >
                    {file.owner}
                </Text>
            </View>
        </Table.Cell>
        <Table.Cell>
            {dateFormat.format(file.modified)}
        </Table.Cell>
        <Table.Cell
            isNumeric={true}
        >
            {formatSize(file.size)}
        </Table.Cell>
        <Table.Cell
            isShrink={true}
        >
            {renderFileMenu(file)}
        </Table.Cell>
    </Table.Row>;

    const renderCard = (file: FileRow) => <Card
        variant={selectedFile === file.id ? "filled" : "outline"}
        style={[
            stylesheet.cell,
            {
                minWidth: shapeConfigs.cardWidth / 2
            }
        ]}
        onPress={handleFileSelect(file.id)}
        isPressable={true}
        key={file.id}
    >
        <Card.Content>
            <View
                style={[
                    stylesheet.person,
                    {
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Avatar
                    icon={file.kind === "image" ? FileImageIcon : FileTextIcon}
                />
                <View
                    style={stylesheet.grow}
                >
                    <Text
                        variant="labelMediumSize"
                        numberOfLines={1}
                        color="high"
                    >
                        {file.name}
                    </Text>
                    <Text
                        variant="bodySmallSize"
                        color="mid"
                    >
                        {`${file.owner} · ${formatSize(file.size)}`}
                    </Text>
                </View>
            </View>
        </Card.Content>
    </Card>;

    const renderBreadcrumb = (key: string, index: number) => <Breadcrumb.Item
        key={key}
    >
        {
            index === path.length - 1 ?
                <Breadcrumb.Page
                    title={t(`blocks.files01.folder.${key}`)}
                />
            :
                <Breadcrumb.Link
                    title={t(`blocks.files01.folder.${key}`)}
                    onPress={handlePress}
                />
        }
    </Breadcrumb.Item>;

    return <Sidebar.Provider
        variant="inset"
    >
        <Sidebar
            title={t("blocks.files01.navTitle")}
            collapseBehaviour="offcanvas"
        >
            <Sidebar.Header>
                <WorkspaceSwitcher
                    onPress={handlePress}
                />
            </Sidebar.Header>
            <Sidebar.Content>
                <Sidebar.Group
                    title={t("blocks.files01.folders")}
                >
                    <TreeView
                        aria-label={t("blocks.files01.folders")}
                        selectedKeys={selectedFolder}
                        expandedKeys={expandedKeys}
                        onExpand={setExpandedKeys}
                        onSelect={handleSelect}
                        selectionMode="single"
                        isGuideVisible={true}
                        nodes={nodes}
                    />
                </Sidebar.Group>
            </Sidebar.Content>
            <Sidebar.Footer>
                <Card
                    variant="outline"
                >
                    <Card.Content>
                        <View
                            style={{
                                gap: spaces.spacingSm
                            }}
                        >
                            <Progress
                                description={t("blocks.files01.storageUsed", [
                                    gigabytes.format(64.2),
                                    gigabytes.format(100)
                                ])}
                                title={t("blocks.files01.storage")}
                                isValueVisible={true}
                                variant="linear"
                                value={64.2}
                                max={100}
                            />
                            <Button
                                title={t("blocks.files01.upgrade")}
                                onPress={handlePress}
                                variant="link"
                                size="small"
                            />
                        </View>
                    </Card.Content>
                </Card>
            </Sidebar.Footer>
        </Sidebar>
        <Sidebar.Inset>
            <View
                style={[
                    stylesheet.bar,
                    {
                        paddingHorizontal: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        paddingVertical: spaces.spacingMd,
                        gap: spaces.spacingSm
                    }
                ]}
            >
                <Sidebar.Trigger/>
                <View
                    style={stylesheet.grow}
                >
                    <Breadcrumb
                        accessibilityLabel={t("blocks.files01.breadcrumb")}
                        size="small"
                    >
                        <Breadcrumb.List>
                            {path.map(renderBreadcrumb)}
                        </Breadcrumb.List>
                    </Breadcrumb>
                </View>
                <TextInput
                    accessibilityLabel={t("blocks.files01.searchPlaceholder")}
                    placeholder={t("blocks.files01.searchPlaceholder")}
                    style={stylesheet.search}
                    onChangeText={setQuery}
                    spreadBehaviour="free"
                    isClearEnabled={true}
                    icon={SearchIcon}
                    value={query}
                    size="small"
                />
                <View>
                    <ToggleGroup
                        title={t("blocks.files01.viewTitle")}
                        onValueChange={handleViewChange}
                        attachBehaviour="attached"
                        isClearEnabled={false}
                        variant="outline"
                        value={view}
                        size="small"
                    >
                        <ToggleGroup.Item
                            accessibilityLabel={t("blocks.files01.listView")}
                            isIconOnly={true}
                            icon={ListIcon}
                            value="list"
                        />
                        <ToggleGroup.Item
                            accessibilityLabel={t("blocks.files01.gridView")}
                            icon={LayoutGridIcon}
                            isIconOnly={true}
                            value="grid"
                        />
                    </ToggleGroup>
                </View>
                <Button
                    title={isCompact ? undefined : t("blocks.files01.newFolder")}
                    accessibilityLabel={t("blocks.files01.newFolder")}
                    spreadBehaviour="free"
                    icon={FolderPlusIcon}
                    onPress={handlePress}
                    variant="outline"
                    type="neutral"
                    size="small"
                />
                <Button
                    title={isCompact ? undefined : t("blocks.files01.upload")}
                    accessibilityLabel={t("blocks.files01.upload")}
                    spreadBehaviour="free"
                    onPress={handlePress}
                    icon={UploadIcon}
                    variant="filled"
                    type="primary"
                    size="small"
                />
            </View>
            <Separator/>
            <View
                style={[
                    stylesheet.content,
                    {
                        padding: isCompact ? spaces.spacingMd : spaces.spacingLg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <FileUpload
                    description={t("blocks.files01.dropHint")}
                    title={t("blocks.files01.dropTitle")}
                    maxSize={50 * MEGABYTE}
                    accept={[
                        "image/png",
                        "image/jpeg",
                        "application/pdf"
                    ]}
                    onChange={setUploads}
                    isMultiple={true}
                    files={uploads}
                />
                {
                    view === "list" ?
                        <Table
                            caption={t("blocks.files01.tableCaption")}
                            isCaptionHidden={true}
                            isRaised={true}
                        >
                            <Table.Header>
                                <Table.Row>
                                    <Table.Head>
                                        {t("blocks.files01.columnName")}
                                    </Table.Head>
                                    <Table.Head>
                                        {t("blocks.files01.columnOwner")}
                                    </Table.Head>
                                    <Table.Head>
                                        {t("blocks.files01.columnModified")}
                                    </Table.Head>
                                    <Table.Head
                                        isNumeric={true}
                                    >
                                        {t("blocks.files01.columnSize")}
                                    </Table.Head>
                                    <Table.Head
                                        isShrink={true}
                                    >
                                        <View
                                            style={{
                                                width: controlHeights.small
                                            }}
                                        />
                                    </Table.Head>
                                </Table.Row>
                            </Table.Header>
                            <Table.Body>
                                {files.map(renderRow)}
                            </Table.Body>
                        </Table>
                    :
                        <View
                            style={[
                                stylesheet.grid,
                                {
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            {files.map(renderCard)}
                        </View>
                }
            </View>
        </Sidebar.Inset>
    </Sidebar.Provider>;
};
export default Files01;
